import React, {
  useState, useContext, useCallback, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { Drawer, DrawerHeader, Button } from '@one-thd/sui-atomic-components';
import { AddToQuote } from '@thd-olt-component-react/add-to-quote';
import { AddToCoreContext, AddToCoreProvider } from './AddToCoreContext';
import { SCREENS } from './coreConstants';
import { triggerAnalytics } from './coreHelpers';
import { TRACK } from '../AddToConstants';
import { AddToSelection as AddToSelectionPicker } from '../common/addto-selection/AddToSelection';
import { ChoosingTheRightTool } from '../common/addto-selection/ChoosingTheRightTool';

// Import content components to render inline within the same drawer
import { AddToListContent, AddToList } from './AddToList';
import { AddToProjectContent, AddToProject } from './AddToProject';

// Screen title mapping — merged from all flows
const SCREEN_TITLES = {
  [SCREENS.ADD_TO_SELECTION]: 'Add to...',
  [SCREENS.CHOOSING_THE_RIGHT_TOOL]: 'Choosing the Right Tool',
  [SCREENS.SIGN_IN]: 'Sign In',
  // List screens
  [SCREENS.LIST_CREATE_LIST]: 'Create List',
  [SCREENS.LIST_SELECT_LIST]: 'Select List',
  [SCREENS.LIST_SELECT_GROUP]: 'Select Group',
  [SCREENS.LIST_SUCCESS]: 'Added to List',
  // Project screens
  [SCREENS.PROJECT_SELECT_PROJECT]: 'Select Project',
  [SCREENS.PROJECT_CREATE_PROJECT]: 'Create Project',
  [SCREENS.PROJECT_ZERO_STATE]: 'Add Items to Project',
  [SCREENS.CREATE_PROJECT_PREVIEW]: 'Add Items to Project',
  [SCREENS.PROJECT_SUCCESS]: 'Added to Project',
  [SCREENS.MY_STORE]: 'My Store',
  [SCREENS.DELIVERY_ZIP]: 'Delivery ZIP Code',
};

/**
 * AddToSelection — combo wrapper that shows the type picker (List/Project/Quote),
 * then routes into the appropriate flow WITHIN THE SAME DRAWER.
 * Uses stack-based navigation via AddToCoreProvider.
 * Does NOT include template-creator features.
 */
const AddToSelectionContent = ({
  products,
  isProjectsEligible,
  addToType,
  onSelectionScreenVisible,
  onTypeSelected,
}) => {
  const {
    screen,
    pushScreen,
    popScreen,
    handleClose,
  } = useContext(AddToCoreContext);

  const listScreens = [
    SCREENS.LIST_CREATE_LIST,
    SCREENS.LIST_SELECT_LIST,
    SCREENS.LIST_SELECT_GROUP,
    SCREENS.LIST_SUCCESS,
    SCREENS.DEFAULT,
  ];
  const projectScreens = [
    SCREENS.PROJECT_SELECT_PROJECT,
    SCREENS.PROJECT_CREATE_PROJECT,
    SCREENS.PROJECT_ZERO_STATE,
    SCREENS.CREATE_PROJECT_PREVIEW,
    SCREENS.PROJECT_SUCCESS,
    SCREENS.MY_STORE,
    SCREENS.DELIVERY_ZIP,
  ];

  useEffect(() => {
    if (!screen) {
      pushScreen(SCREENS.ADD_TO_SELECTION);
    }
  }, [screen, pushScreen]);

  useEffect(() => {
    if (screen === SCREENS.ADD_TO_SELECTION && onSelectionScreenVisible) {
      onSelectionScreenVisible();
    }
  }, [onSelectionScreenVisible, screen]);

  const handleSelection = useCallback((type) => {
    if (onTypeSelected) {
      onTypeSelected(type);
    }
    if (type !== 'Quote') {
      pushScreen(SCREENS.LOADING);
    }
  }, [onTypeSelected, pushScreen]);

  if (screen === SCREENS.ADD_TO_SELECTION) {
    return (
      <AddToSelectionPicker
        products={products}
        addToType={addToType}
        setAddToType={handleSelection}
        handleClose={handleClose}
        isProjectsEligible={isProjectsEligible}
      />
    );
  }

  if (screen === SCREENS.CHOOSING_THE_RIGHT_TOOL) {
    return (
      <ChoosingTheRightTool
        onBack={() => popScreen()}
        onClose={handleClose}
      />
    );
  }

  if (
    addToType === 'List'
    && (screen === SCREENS.LOADING || screen === SCREENS.SIGN_IN || listScreens.includes(screen))
  ) {
    return <AddToListContent products={products} />;
  }

  if (
    addToType === 'Project'
    && (screen === SCREENS.LOADING || screen === SCREENS.SIGN_IN || projectScreens.includes(screen))
  ) {
    return <AddToProjectContent products={products} />;
  }

  return null;
};

AddToSelectionContent.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({})),
  isProjectsEligible: PropTypes.bool,
  addToType: PropTypes.string,
  onSelectionScreenVisible: PropTypes.func,
  onTypeSelected: PropTypes.func,
};

AddToSelectionContent.defaultProps = {
  products: [],
  isProjectsEligible: false,
  addToType: null,
  onSelectionScreenVisible: null,
  onTypeSelected: null,
};

export const AddToSelection = (props) => {
  const {
    products,
    isProjectsEligible,
    renderButton,
    defaultOpen,
    storeId: storeIdProp
  } = props;

  const [open, setOpen] = useState(defaultOpen || false);
  const [addToType, setAddToType] = useState(null);
  const [selectedProductDetails, setSelectedProductDetails] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const addToQuoteRef = useRef(null);

  const store = useStore();
  const storeId = storeIdProp || store?.storeId;
  const { isLoggedIn, customerType } = useThdCustomer() || {};
  const isB2B = isProjectsEligible || customerType === 'B2B';

  const handleOpen = () => {
    setOpen(true);
    setAddToType(null);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
  };

  const handleClose = () => {
    setOpen(false);
    setAddToType(null);
  };

  const handleSelectionScreenVisible = () => {
    setAddToType(null);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
  };

  // When user picks a type, keep drawer open and switch content
  const handleTypeSelection = useCallback((type) => {
    if (type === 'Quote') {
      // Quote opens externally — trigger ATQ then close selection drawer
      if (addToQuoteRef.current) {
        addToQuoteRef.current.click();
      }
      handleClose();
      return;
    }
    setAddToType(type);
  }, []);

  const coreValue = {
    addToType: addToType || 'List',
    products,
    storeId,
    onClose: handleClose,
    selectedProductDetails,
    setSelectedProductDetails,
    showSuccessMessage,
    setShowSuccessMessage,
    showErrorMessage,
    setShowErrorMessage,
    selectedGroup,
    setSelectedGroup,
    listRefetch: () => {},
    projectRefetch: () => {},
  };

  const renderTriggerButton = () => {
    if (renderButton) {
      return renderButton({ onClick: handleOpen });
    }
    if (defaultOpen) return null;
    return (
      <Button
        fullWidth
        variant="secondary"
        onClick={handleOpen}
        disabled={!products?.length}
        aria-label="Add to..."
        data-testid="core-add-to-selection-button"
      >
        Add to...
      </Button>
    );
  };

  // Determine drawer title from context screen
  const getDrawerTitle = () => {
    return SCREEN_TITLES[SCREENS.ADD_TO_SELECTION] || 'Add to...';
  };

  return (
    <>
      {renderTriggerButton()}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <Drawer open={open} onClose={handleClose} onClick={(e) => e.stopPropagation()}>
        <AddToCoreProvider value={coreValue}>
          <AddToSelectionDrawerContent
            products={products}
            isProjectsEligible={isB2B}
            addToType={addToType}
            onTypeSelected={handleTypeSelection}
            onSelectionScreenVisible={handleSelectionScreenVisible}
            onClose={handleClose}
          />
        </AddToCoreProvider>
      </Drawer>
      <div className="sui-hidden">
        <AddToQuote
          buttonRef={addToQuoteRef}
          itemId={products[0]?.itemId}
          quantity={products[0]?.quantity || 1}
        />
      </div>
    </>
  );
};

/**
 * Inner component that has access to AddToCoreContext for header rendering.
 */
const AddToSelectionDrawerContent = ({
  products,
  isProjectsEligible,
  addToType,
  onTypeSelected,
  onSelectionScreenVisible,
  onClose,
}) => {
  const {
    screen,
    popScreen,
    canGoBack,
  } = useContext(AddToCoreContext);

  const showBack = canGoBack && screen !== SCREENS.ADD_TO_SELECTION;

  const handleBackPress = () => {
    triggerAnalytics(TRACK.ADD_TO_OVERLAY_EXIT, { screen });
    popScreen();
  };

  // The selection picker renders its own DrawerHeader
  const showHeader = screen
    && screen !== SCREENS.ADD_TO_SELECTION
    && screen !== SCREENS.CHOOSING_THE_RIGHT_TOOL;

  return (
    <>
      {showHeader && (
        <DrawerHeader
          data-testid="core-add-to-selection-header"
          title={SCREEN_TITLES[screen] || 'Add to...'}
          onClose={onClose}
          onBack={showBack ? handleBackPress : null}
        />
      )}
      <AddToSelectionContent
        products={products}
        isProjectsEligible={isProjectsEligible}
        addToType={addToType}
        onSelectionScreenVisible={onSelectionScreenVisible}
        onTypeSelected={onTypeSelected}
      />
    </>
  );
};

AddToSelectionDrawerContent.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({})),
  isProjectsEligible: PropTypes.bool,
  addToType: PropTypes.string,
  onTypeSelected: PropTypes.func,
  onSelectionScreenVisible: PropTypes.func,
  onClose: PropTypes.func,
};

AddToSelectionDrawerContent.defaultProps = {
  products: [],
  isProjectsEligible: false,
  addToType: null,
  onTypeSelected: null,
  onSelectionScreenVisible: null,
  onClose: null,
};

AddToSelection.displayName = 'AddToSelection';

AddToSelection.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
  })),
  isProjectsEligible: PropTypes.bool,
  renderButton: PropTypes.func,
  defaultOpen: PropTypes.bool,
  storeId: PropTypes.string,
};

AddToSelection.defaultProps = {
  products: [],
  isProjectsEligible: false,
  renderButton: null,
  defaultOpen: false,
  storeId: null,
};

AddToSelection.dataModel = extend(
  AddToList,
  AddToProject
);
